import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import FundPicker from "./FundPicker";
import { Layers, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { latestWeights, type RangeFund, type WeightRow } from "./rangeFunds";

export type BatchTradeType = "asset_switch" | "rebalance";

export interface BatchPortfolioConfig {
  num: number;
  label: string;
}

export interface BatchTradeDialogProps {
  tradesTable: "ba_trades" | "ba_r_trades" | "ba_dist_trades" | "ba_mm_trades";
  weightsTable: "ba_cbam_fund_weights" | "ba_r_cbam_fund_weights" | "ba_dist_cbam_fund_weights" | "ba_mm_cbam_fund_weights";
  portfolios: BatchPortfolioConfig[];
  /** Annotated against this range by useRangeFunds — includes previously-held funds so a sold
   *  position can be bought back here as well as in the single-trade dialog. */
  funds: RangeFund[];
  invalidateKeys: string[][];
  /** On a SELL that takes every portfolio to zero, stamp an explicit zero row across the whole
   *  range so the exit is dated. Never deletes — see the block in `save` for why. */
  zeroOutOnFullExit?: boolean;
  triggerLabel?: string;
  onAfterSave?: (batchId: string) => Promise<void> | void;
}

interface BatchRow {
  fund_id: string;
  direction: "BUY" | "SELL";
  // In asset_switch mode: delta % to add/subtract.
  // In rebalance mode: target weight %.
  pcts: Record<number, number>;
  includes: Record<number, boolean>;
}

function makeRow(portfolios: BatchPortfolioConfig[]): BatchRow {
  return {
    fund_id: "",
    direction: "BUY",
    pcts: Object.fromEntries(portfolios.map((p) => [p.num, 0])),
    includes: Object.fromEntries(portfolios.map((p) => [p.num, false])),
  };
}

export default function BatchTradeDialog({
  tradesTable, weightsTable, portfolios, funds, invalidateKeys, zeroOutOnFullExit, triggerLabel, onAfterSave,
}: BatchTradeDialogProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tradeType, setTradeType] = useState<BatchTradeType>("asset_switch");
  const [tradeDate, setTradeDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [markerOnly, setMarkerOnly] = useState(false);
  const [inputMode, setInputMode] = useState<"target" | "delta">("target");
  const [rows, setRows] = useState<BatchRow[]>([makeRow(portfolios), makeRow(portfolios)]);

  // All weight rows for this range. Resolution to "the weight that applied" is deliberately NOT
  // done in the query: it depends on tradeDate, which the user can change while the dialog is
  // open, so it is derived below instead of being baked into the cache key.
  const { data: weightRows } = useQuery({
    queryKey: ["batch-latest-weights", weightsTable, open],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(weightsTable)
        .select("fund_id, portfolio, weight_pct, effective_date")
        .order("effective_date", { ascending: false });
      if (error) throw error;
      return ((data || []) as unknown) as WeightRow[];
    },
  });

  // The weight in force AS OF the chosen trade date, not the newest row in the table.
  //
  // Resolving table-wide let a back-dated trade be priced off — and stamped in front of — rows
  // that are in its own future: the dialog would compute the delta from today's weight, then
  // write the result at the back-date, so the preview, the stored trade pct and the exit test all
  // described a position the fund did not hold on that date. With the bound, a back-dated trade
  // sees the book as it stood then, and later rows are left alone.
  const asOfWeights = useMemo(
    () => latestWeights(weightRows || [], tradeDate),
    [weightRows, tradeDate],
  );

  const currentOf = (fundId: string, p: number) => asOfWeights.get(`${fundId}|${p}`) ?? 0;

  const reset = () => {
    setTradeType("asset_switch");
    setTradeDate(new Date().toISOString().split("T")[0]);
    setNotes("");
    setMarkerOnly(false);
    setInputMode("target");
    setRows([makeRow(portfolios), makeRow(portfolios)]);
  };

  const updateRow = (idx: number, patch: Partial<BatchRow>) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const updateRowAlloc = (idx: number, p: number, key: "pct" | "incl", value: number | boolean) => {
    setRows((rs) => rs.map((r, i) => {
      if (i !== idx) return r;
      if (key === "pct") return { ...r, pcts: { ...r.pcts, [p]: value as number } };
      return { ...r, includes: { ...r.includes, [p]: value as boolean } };
    }));
  };

  const save = useMutation({
    mutationFn: async () => {
      const batchId = crypto.randomUUID();

      if (markerOnly) {
        const payload: any = {
          trade_date: tradeDate,
          fund_id: null,
          direction: "MARKER",
          notes: notes || null,
          trade_type: tradeType,
          batch_id: batchId,
        };
        for (const p of portfolios) {
          payload[`portfolio_${p.num}_included`] = false;
          payload[`portfolio_${p.num}_pct`] = 0;
        }
        const { error } = await supabase.from(tradesTable).insert(payload);
        if (error) throw error;
        return batchId;
      }

      const valid = rows.filter((r) => r.fund_id);
      if (!valid.length) throw new Error("Add at least one trade row with a fund selected");

      for (const row of valid) {
        // Compute target + signed delta per portfolio based on input mode.
        const deltaByPortfolio: Record<number, number> = {};
        const targetByPortfolio: Record<number, number> = {};
        for (const p of portfolios) {
          if (!row.includes[p.num]) continue;
          const input = Number(row.pcts[p.num] || 0);
          const current = currentOf(row.fund_id, p.num);
          let target: number;
          if (inputMode === "target") {
            target = input;
          } else if (tradeType === "rebalance") {
            // Delta mode for rebalance: signed input
            target = current + input;
          } else {
            // Delta mode for asset_switch: signed by row direction
            const sign = row.direction === "BUY" ? 1 : -1;
            target = current + sign * input;
          }
          targetByPortfolio[p.num] = Math.max(0, +target.toFixed(6));
          deltaByPortfolio[p.num] = +(targetByPortfolio[p.num] - current).toFixed(6);
        }

        // Row-level direction: derived from net delta when in target mode or rebalance;
        // otherwise honour the user's explicit BUY/SELL for asset_switch + delta.
        let rowDirection: "BUY" | "SELL" = row.direction;
        if (inputMode === "target" || tradeType === "rebalance") {
          const net = Object.values(deltaByPortfolio).reduce((a, b) => a + b, 0);
          rowDirection = net >= 0 ? "BUY" : "SELL";
        }

        const payload: any = {
          trade_date: tradeDate,
          fund_id: row.fund_id,
          direction: rowDirection,
          notes: notes || null,
          trade_type: tradeType,
          batch_id: batchId,
        };
        for (const p of portfolios) {
          const included = !!row.includes[p.num];
          payload[`portfolio_${p.num}_included`] = included;
          // Store the magnitude of the realised delta as the trade pct.
          payload[`portfolio_${p.num}_pct`] = included ? Math.abs(deltaByPortfolio[p.num] || 0) : 0;
        }
        const { error } = await supabase.from(tradesTable).insert(payload);
        if (error) throw error;

        // Update weights per portfolio to the computed target.
        //
        // Bounded to rows dated on or before the trade date. Unbounded, this read decided the
        // full-exit test from the newest row in the table regardless of date, while the zeros it
        // then wrote were stamped at the user's chosen tradeDate — so a back-dated sell inserted a
        // zero IN FRONT of rows that still showed the fund held, and every reconstruction between
        // the two dates lost the position. The exit must be judged as of the trade date.
        const { data: existingWeights } = await supabase
          .from(weightsTable)
          .select("portfolio, weight_pct, effective_date")
          .eq("fund_id", row.fund_id)
          .lte("effective_date", tradeDate)
          .order("effective_date", { ascending: false });
        const latestByPortfolio = new Map<number, number>();
        for (const w of (existingWeights || []) as any[]) {
          if (!latestByPortfolio.has(w.portfolio)) latestByPortfolio.set(w.portfolio, Number(w.weight_pct) || 0);
        }
        const newWeights: Record<number, number> = {};
        for (const p of portfolios) {
          const current = latestByPortfolio.get(p.num) || 0;
          const included = !!row.includes[p.num];
          const next = included ? (targetByPortfolio[p.num] ?? current) : current;
          newWeights[p.num] = next;
          if (included) {
            const { error: wErr } = await supabase
              .from(weightsTable)
              .upsert(
                { fund_id: row.fund_id, portfolio: p.num, weight_pct: next, effective_date: tradeDate },
                { onConflict: "fund_id,portfolio,effective_date" }
              );
            if (wErr) throw wErr;
          }
        }
        if (zeroOutOnFullExit && rowDirection === "SELL") {
          const finalWeights = portfolios.map((p) =>
            row.includes[p.num] ? newWeights[p.num] : (latestByPortfolio.get(p.num) || 0)
          );
          if (finalWeights.every((w) => w <= 0.0001)) {
            // Full exit. NEVER delete here. The weight tables are effective-dated and every
            // historical reconstruction reads "the latest row per fund on or before date X"
            // (src/lib/blendedPortfolioMatrix.ts), so removing the fund's rows erases its entire
            // opening position; deleting ba_cbam_funds is worse still, because
            // ba_cbam_fund_weights_fund_id_fkey is ON DELETE CASCADE. The schema's exit
            // convention is an explicit weight_pct = 0 row at the exit date — already written
            // above for every included portfolio, so only the untouched ones need stamping.
            // The fund row and its sector mappings stay: trg_sync_is_current flips
            // ba_cbam_funds.is_current to false off the zero rows, and the read paths hide
            // zero-weight holdings.
            //
            // That flip is REVERSIBLE and derived, not a latch — the same trigger sets it back to
            // true when a later non-zero row is written. The fund therefore stays selectable in
            // this dialog (see rangeFunds.ts) so the position can be bought back.
            const exitRows = portfolios
              .filter((p) => !row.includes[p.num])
              .map((p) => ({ fund_id: row.fund_id, portfolio: p.num, weight_pct: 0, effective_date: tradeDate }));
            if (exitRows.length) {
              const { error: xErr } = await supabase
                .from(weightsTable)
                .upsert(exitRows, { onConflict: "fund_id,portfolio,effective_date" });
              if (xErr) throw xErr;
            }
          }
        }
      }
      return batchId;
    },
    onSuccess: async (batchId) => {
      if (onAfterSave && batchId) {
        try { await onAfterSave(batchId); } catch (e) { console.error("onAfterSave error", e); }
      }
      for (const k of invalidateKeys) queryClient.invalidateQueries({ queryKey: k });
      toast.success(markerOnly ? "Rebalance marker logged" : `Batch logged (${rows.filter((r) => r.fund_id).length} trades)`);
      setOpen(false);
      reset();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const isRebalance = tradeType === "rebalance";
  const fundById = useMemo(() => new Map(funds.map((f) => [f.id, f])), [funds]);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline"><Layers className="h-4 w-4 mr-1" /> {triggerLabel || "Batch Add"}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Batch Add Trades</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Trade Type</Label>
              <Select value={tradeType} onValueChange={(v) => setTradeType(v as BatchTradeType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="asset_switch">Ad-Hoc Asset Switch</SelectItem>
                  <SelectItem value="rebalance">Whole Portfolio Rebalance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Trade Date</Label>
              <Input type="date" value={tradeDate} onChange={(e) => setTradeDate(e.target.value)} />
          </div>

          <p className="text-xs text-muted-foreground">
            {inputMode === "target"
              ? (isRebalance
                  ? "Rebalance · Target mode: enter the desired final weight per portfolio. The delta vs current is computed and stored."
                  : "Asset Switch · Target mode: enter the desired final weight per portfolio. Direction (BUY/SELL) is derived from the sign of the delta.")
              : (isRebalance
                  ? "Rebalance · Delta mode: enter the change (+ to buy, − to sell). The resulting target weight is computed and stored."
                  : "Asset Switch · Delta mode: % entered is added (BUY) or subtracted (SELL) from the current weight.")}
          </p>

            <div>
              <Label>Batch Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Shared note for all rows" />
            </div>
          </div>

          <div className={isRebalance ? "grid grid-cols-2 gap-3" : ""}>
            <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/30">
              <Label className="text-sm font-medium">Input as</Label>
              <Select value={inputMode} onValueChange={(v) => setInputMode(v as "target" | "delta")}>
                <SelectTrigger className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="target">Target weight (%)</SelectItem>
                  <SelectItem value="delta">Delta (± %)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isRebalance && (
              <div className="flex items-start gap-2 p-3 border rounded-md bg-muted/30">
                <Checkbox
                  id="marker-only"
                  checked={markerOnly}
                  onCheckedChange={(v) => setMarkerOnly(!!v)}
                  className="mt-0.5"
                />
                <div className="space-y-0.5">
                  <Label htmlFor="marker-only" className="text-sm font-medium cursor-pointer">
                    Rebalance marker only (no weight changes)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Logs a dated rebalance event on the timeline without altering any holdings or weights.
                  </p>
                </div>
              </div>
            )}
          </div>

          {!markerOnly && (
          <div className="border rounded-md overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2 font-medium text-muted-foreground">Fund</th>
                  {inputMode === "delta" && !isRebalance && (
                    <th className="text-center p-2 font-medium text-muted-foreground w-20">Dir</th>
                  )}
                  {portfolios.map((p) => (
                    <th key={p.num} className="text-center p-2 font-medium text-muted-foreground">
                      {p.label}
                      <div className="text-[10px] font-normal opacity-70">current → target = Δ</div>
                    </th>
                  ))}
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} className="border-t align-top">
                    <td className="p-2 min-w-[260px]">
                      <FundPicker
                        value={row.fund_id}
                        onValueChange={(v) => updateRow(idx, { fund_id: v })}
                        funds={funds}
                        triggerClassName="h-8 text-xs"
                        verbose
                      />
                      {fundById.get(row.fund_id)?.previouslyHeldInRange && (
                        <div className="mt-1 text-[10px] leading-tight text-amber-600 dark:text-amber-500">
                          Previously held — a buy re-opens the position.
                        </div>
                      )}
                    </td>
                    {inputMode === "delta" && !isRebalance && (
                      <td className="p-2">
                        <Select value={row.direction} onValueChange={(v) => updateRow(idx, { direction: v as "BUY" | "SELL" })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="BUY">BUY</SelectItem>
                            <SelectItem value="SELL">SELL</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    )}
                    {portfolios.map((p) => {
                      const current = row.fund_id ? currentOf(row.fund_id, p.num) : 0;
                      const input = Number(row.pcts[p.num] || 0);
                      let target: number;
                      let delta: number;
                      if (inputMode === "target") {
                        target = input;
                        delta = +(target - current).toFixed(4);
                      } else if (isRebalance) {
                        target = +(current + input).toFixed(4);
                        delta = input;
                      } else {
                        const sign = row.direction === "BUY" ? 1 : -1;
                        target = +(current + sign * input).toFixed(4);
                        delta = +(sign * input).toFixed(4);
                      }
                      const deltaColor = delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-muted-foreground";
                      return (
                        <td key={p.num} className="p-2 text-center">
                          <div className="flex items-center gap-1 justify-center">
                            <Checkbox
                              checked={!!row.includes[p.num]}
                              onCheckedChange={(v) => updateRowAlloc(idx, p.num, "incl", !!v)}
                            />
                            <Input
                              type="number"
                              step="0.01"
                              className="w-20 h-8 text-xs"
                              value={row.pcts[p.num]}
                              onChange={(e) => updateRowAlloc(idx, p.num, "pct", parseFloat(e.target.value) || 0)}
                              disabled={!row.includes[p.num]}
                            />
                          </div>
                          {row.includes[p.num] && row.fund_id && (
                            <div className="mt-1 text-[10px] leading-tight">
                              <span className="text-muted-foreground">{current.toFixed(2)}% → {Math.max(0, target).toFixed(2)}%</span>
                              <span className={`ml-1 font-medium ${deltaColor}`}>
                                {delta > 0 ? "+" : ""}{delta.toFixed(2)}%
                              </span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="p-2 text-center">
                      <Button
                        type="button" variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => setRows((rs) => rs.filter((_, i) => i !== idx))}
                        disabled={rows.length <= 1}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          <div className="flex items-center justify-between">
            {!markerOnly && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setRows((rs) => [...rs, makeRow(portfolios)])}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Row
              </Button>
            )}
            {markerOnly && <div />}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save Batch"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function tradeTypeLabel(t: string | null | undefined): string {
  if (t === "asset_switch") return "Asset Switch";
  if (t === "rebalance") return "Rebalance";
  return "—";
}
